import { COLORS, FONTS } from "../../../../../lib/brand";
import { PANE_THEMES } from "../../../../../components/brand/paneThemes";
import { useFocusEffect, useLocalSearchParams } from "expo-router";
import { useCallback, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
  type TextStyle,
  type ViewStyle,
} from "react-native";

import { getClientCompetition } from "../../../../../lib/clientCompetitions";
import {
  extendInterviewOffer,
  getCompetitionPipeline,
  withdrawInterviewOffer,
  type InterviewOfferStatus,
  type PipelineRow,
} from "../../../../../lib/interviewOffers";
import { useRequireApprovedCase } from "../../../../../lib/useRequireApprovedCase";
import type { ClientCompetitionListRow } from "../../../../../types/database";

const THEME = PANE_THEMES.Client;
const FOREST = THEME.sidebarBg;
const FOREST_MID = COLORS.FOREST_MID.hex;
const FOREST_DEEP = COLORS.FOREST_DEEP.hex;
const AMBER = THEME.accent;
const SAGE = COLORS.SAGE.hex;
const LINEN = THEME.textOnSurface;

const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const BORDER = "rgba(255,255,255,0.07)";
const RED = THEME.destructive;

const PAGE_MAX_W = 1240;

type SortKey = "score" | "recent" | "interest";
const SORT_LABELS: Record<SortKey, string> = {
  score: "Highest weighted score",
  recent: "Recently opted in",
  interest: "Most opted-in roles",
};

type FilterKey = "all" | "no_offer" | "passed" | "failed";

const CATEGORY_SHORT_LABELS: Record<string, string> = {
  team_efficiency: "Team eff",
  deliverable_quality: "Quality",
  innovation_solution: "Innovation",
  presentation_communication: "Presentation",
};

const OFFER_STATUS_LABEL: Record<InterviewOfferStatus, string> = {
  extended: "Offer extended",
  viewed: "Offer viewed",
  accepted: "Accepted",
  declined: "Declined",
  withdrawn: "Withdrawn",
};

export default function PipelineScreen() {
  // Route guard — pre-approval cases bounce to /edit (no pipeline yet).
  useRequireApprovedCase();
  const { id } = useLocalSearchParams<{ id: string }>();
  const competitionId = typeof id === "string" ? id : "";

  const [comp, setComp] = useState<ClientCompetitionListRow | null>(null);
  const [rows, setRows] = useState<PipelineRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [gated, setGated] = useState(false);

  // Filter state
  const [positionFilter, setPositionFilter] = useState<string>("all");
  const [sort, setSort] = useState<SortKey>("score");
  const [filterChip, setFilterChip] = useState<FilterKey>("all");

  const [inviteTarget, setInviteTarget] = useState<PipelineRow | null>(null);

  const load = useCallback(async () => {
    if (!competitionId) return;
    setLoading(true);
    setErrMsg(null);
    try {
      const c = await getClientCompetition(competitionId);
      setComp(c);
      // The RPC enforces "closed only" — so before judging closes the
      // call returns an error. Branch the UI on that instead of
      // surfacing a raw 42501.
      if (c?.status !== "closed") {
        setGated(true);
        setRows([]);
        return;
      }
      setGated(false);
      const { rows: pipelineRows, error } = await getCompetitionPipeline(competitionId);
      if (error) {
        setErrMsg(error.message);
        setRows([]);
      } else {
        setRows(pipelineRows);
      }
    } finally {
      setLoading(false);
    }
  }, [competitionId]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  // Build the "All positions" + per-position picker options.
  const positions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of rows) {
      if (!seen.has(r.position_id)) seen.set(r.position_id, r.position_title);
    }
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  // Per-position aggregate counts feed the "Most opted-in" sort and the
  // filter chip predicates. Computed once per render.
  const positionCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of rows) counts.set(r.position_id, (counts.get(r.position_id) ?? 0) + 1);
    return counts;
  }, [rows]);

  const filteredAndSorted = useMemo(() => {
    let list = rows.slice();
    if (positionFilter !== "all") list = list.filter((r) => r.position_id === positionFilter);
    if (filterChip === "no_offer") list = list.filter((r) => !r.offer_id);
    if (filterChip === "passed") list = list.filter((r) => r.passed_gate === true);
    if (filterChip === "failed") list = list.filter((r) => r.passed_gate === false);

    list.sort((a, b) => {
      if (sort === "score") {
        const sa = a.weighted_score ?? -1;
        const sb = b.weighted_score ?? -1;
        if (sa !== sb) return sb - sa;
        return (a.student_name ?? "").localeCompare(b.student_name ?? "");
      }
      if (sort === "recent") {
        return (b.interest_opted_in_at ?? "").localeCompare(a.interest_opted_in_at ?? "");
      }
      // interest: by descending count of opt-ins for the row's position
      const ca = positionCounts.get(a.position_id) ?? 0;
      const cb = positionCounts.get(b.position_id) ?? 0;
      if (ca !== cb) return cb - ca;
      const sa = a.weighted_score ?? -1;
      const sb = b.weighted_score ?? -1;
      return sb - sa;
    });
    return list;
  }, [rows, positionFilter, filterChip, sort, positionCounts]);

  const groupByPosition = positionFilter === "all";
  const grouped = useMemo(() => {
    if (!groupByPosition) return [{ position_id: positionFilter, position_title: positions.find((p) => p[0] === positionFilter)?.[1] ?? "Position", rows: filteredAndSorted }];
    const map = new Map<string, { position_id: string; position_title: string; rows: PipelineRow[] }>();
    for (const r of filteredAndSorted) {
      const k = r.position_id;
      const ex = map.get(k);
      if (ex) ex.rows.push(r);
      else map.set(k, { position_id: k, position_title: r.position_title, rows: [r] });
    }
    return Array.from(map.values());
  }, [filteredAndSorted, groupByPosition, positionFilter, positions]);

  if (loading && rows.length === 0) {
    return (
      <View style={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
        <ActivityIndicator color={AMBER} />
      </View>
    );
  }

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 80 }}>
      <View style={{ paddingHorizontal: 32, paddingTop: 26, maxWidth: PAGE_MAX_W }}>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <View style={{ width: 22, height: 2, backgroundColor: AMBER, opacity: 0.85 }} />
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9.5,
              letterSpacing: 2,
              textTransform: "uppercase",
              color: AMBER,
            }}
          >
            Candidate pipeline
          </Text>
        </View>
        <Text
          style={{
            marginTop: 6,
            fontFamily: FONTS.display,
            fontSize: 32,
            lineHeight: 36,
            color: W,
          }}
        >
          Who's interested, by role
        </Text>
        <Text style={{ marginTop: 6, fontFamily: FONTS.body, fontSize: 13, color: WM, maxWidth: 720 }}>
          Ranked candidate-by-role view of who opted in for which position, with their full Coravu
          score breakdown. Extend interview offers from here — students get an email and a
          dashboard tile, and you'll be notified the moment they accept or decline.
        </Text>

        {gated ? (
          <View
            style={{
              marginTop: 24,
              padding: 24,
              borderRadius: 8,
              backgroundColor: FOREST_DEEP,
              borderWidth: 1,
              borderColor: BORDER,
            }}
          >
            <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 22, color: LINEN }}>
              Pipeline unlocks after judging closes
            </Text>
            <Text
              style={{
                marginTop: 6,
                fontFamily: FONTS.body,
                fontSize: 13,
                lineHeight: 20,
                color: WS,
                maxWidth: 600,
              }}
            >
              Until then, candidate→position interest data is private to the students themselves.
              We'll email you when the pipeline is ready, and you can come back here to extend
              interview offers.
            </Text>
            <Text
              style={{
                marginTop: 10,
                fontFamily: FONTS.body,
                fontSize: 11.5,
                color: WL,
              }}
            >
              Current status: {comp?.status ?? "—"}.
            </Text>
          </View>
        ) : errMsg ? (
          <View
            style={{
              marginTop: 16,
              paddingHorizontal: 14,
              paddingVertical: 12,
              borderRadius: 3,
              backgroundColor: "rgba(230,154,138,0.08)",
              borderWidth: 1,
              borderColor: "rgba(230,154,138,0.4)",
            }}
          >
            <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12.5, color: RED }}>{errMsg}</Text>
          </View>
        ) : rows.length === 0 ? (
          <View
            style={{
              marginTop: 24,
              padding: 24,
              borderRadius: 8,
              backgroundColor: FOREST_DEEP,
              borderWidth: 1,
              borderStyle: "dashed",
              borderColor: BORDER,
            }}
          >
            <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 18, color: LINEN }}>
              No candidates opted in
            </Text>
            <Text style={{ marginTop: 6, fontFamily: FONTS.body, fontSize: 12.5, color: WM }}>
              Add positions to this competition next time and students will see them at registration.
            </Text>
          </View>
        ) : (
          <>
            <View style={{ marginTop: 22, flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
              <FilterPill
                label="All positions"
                active={positionFilter === "all"}
                onPress={() => setPositionFilter("all")}
              />
              {positions.map(([pid, title]) => (
                <FilterPill
                  key={pid}
                  label={title}
                  active={positionFilter === pid}
                  onPress={() => setPositionFilter(pid)}
                />
              ))}
            </View>

            <View style={{ marginTop: 12, flexDirection: "row", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
              <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: WM }}>Sort:</Text>
              {(Object.keys(SORT_LABELS) as SortKey[]).map((k) => (
                <FilterPill
                  key={k}
                  label={SORT_LABELS[k]}
                  active={sort === k}
                  onPress={() => setSort(k)}
                />
              ))}
            </View>

            <View style={{ marginTop: 8, flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
              <FilterChip
                label="All"
                active={filterChip === "all"}
                onPress={() => setFilterChip("all")}
              />
              <FilterChip
                label="Not yet offered"
                active={filterChip === "no_offer"}
                onPress={() => setFilterChip("no_offer")}
              />
              <FilterChip
                label="Passed Coravu"
                active={filterChip === "passed"}
                tone="sage"
                onPress={() => setFilterChip("passed")}
              />
              <FilterChip
                label="Failed Coravu"
                active={filterChip === "failed"}
                tone="red"
                onPress={() => setFilterChip("failed")}
              />
            </View>

            <View style={{ marginTop: 22, gap: 22 }}>
              {grouped.map((g) => (
                <View key={g.position_id}>
                  {groupByPosition ? (
                    <Text
                      style={{
                        fontFamily: FONTS.bodyBold,
                        fontSize: 9.5,
                        letterSpacing: 1.6,
                        textTransform: "uppercase",
                        color: AMBER,
                        marginBottom: 8,
                      }}
                    >
                      {g.position_title} · {g.rows.length} interested
                    </Text>
                  ) : null}
                  <View style={{ gap: 10 }}>
                    {g.rows.map((row) => (
                      <CandidateCard
                        key={row.position_id + row.student_id}
                        row={row}
                        onInvite={() => setInviteTarget(row)}
                        onAfterAction={() => void load()}
                      />
                    ))}
                  </View>
                </View>
              ))}
            </View>
          </>
        )}
      </View>

      {inviteTarget && comp ? (
        <InviteModal
          target={inviteTarget}
          competition={{ id: comp.id, name: comp.name, company_id: comp.company_id, company_name: comp.company_name ?? null }}
          onClose={() => setInviteTarget(null)}
          onSent={() => {
            setInviteTarget(null);
            void load();
          }}
        />
      ) : null}
    </ScrollView>
  );
}

function CandidateCard({
  row,
  onInvite,
  onAfterAction,
}: {
  row: PipelineRow;
  onInvite: () => void;
  onAfterAction: () => void;
}) {
  const [withdrawArmed, setWithdrawArmed] = useState(false);
  const [busy, setBusy] = useState(false);

  async function withdraw() {
    if (!row.offer_id || busy) return;
    setBusy(true);
    const { error } = await withdrawInterviewOffer(row.offer_id);
    setBusy(false);
    if (!error) onAfterAction();
  }

  const breakdownEntries = Object.entries(row.category_breakdown);
  const offer = row.offer_status;

  return (
    <View
      style={{
        padding: 18,
        borderRadius: 6,
        backgroundColor: FOREST_DEEP,
        borderWidth: 1,
        borderColor: BORDER,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "flex-start", gap: 16 }}>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 22, color: LINEN }}>
            {row.student_name ?? row.student_email ?? "Candidate"}
          </Text>
          <Text style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 12, color: WM }}>
            {[row.student_university, row.student_major, row.student_grad_year ? "Grad " + row.student_grad_year : null]
              .filter(Boolean)
              .join(" · ") || "—"}
          </Text>
        </View>
        <View style={{ alignItems: "flex-end" }}>
          {row.weighted_score != null ? (
            <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 30, color: AMBER }}>
              {row.weighted_score.toFixed(2)}
            </Text>
          ) : (
            <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 22, color: WL }}>—</Text>
          )}
          <Text style={{ fontFamily: FONTS.body, fontSize: 10.5, color: WL }}>/ 10</Text>
        </View>
      </View>

      {breakdownEntries.length > 0 ? (
        <Text
          style={{
            marginTop: 6,
            fontFamily: FONTS.body,
            fontSize: 11,
            color: WS,
          }}
        >
          {["team_efficiency", "deliverable_quality", "innovation_solution", "presentation_communication"]
            .map((slug) =>
              row.category_breakdown[slug] != null
                ? `${CATEGORY_SHORT_LABELS[slug] ?? slug} ${(row.category_breakdown[slug] as number).toFixed(1)}`
                : null
            )
            .filter(Boolean)
            .join(" · ")}
        </Text>
      ) : null}

      <View style={{ marginTop: 12, flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <View
          style={{
            paddingHorizontal: 9,
            paddingVertical: 3,
            borderRadius: 999,
            borderWidth: 1,
            borderColor: BORDER,
            backgroundColor: "rgba(255,255,255,0.04)",
          }}
        >
          <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 10.5, color: WS }}>
            Interested in {row.position_title}
          </Text>
        </View>
        {row.passed_gate === true ? (
          <View
            style={{
              paddingHorizontal: 9,
              paddingVertical: 3,
              borderRadius: 999,
              borderWidth: 1,
              borderColor: SAGE,
            }}
          >
            <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 9, letterSpacing: 1.2, color: SAGE }}>
              Passed
            </Text>
          </View>
        ) : row.passed_gate === false ? (
          <View
            style={{
              paddingHorizontal: 9,
              paddingVertical: 3,
              borderRadius: 999,
              borderWidth: 1,
              borderColor: RED,
            }}
          >
            <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 9, letterSpacing: 1.2, color: RED }}>
              Failed
            </Text>
          </View>
        ) : null}
        {offer ? (
          <View
            style={{
              paddingHorizontal: 9,
              paddingVertical: 3,
              borderRadius: 999,
              borderWidth: 1,
              borderColor:
                offer === "accepted"
                  ? SAGE
                  : offer === "declined" || offer === "withdrawn"
                    ? WL
                    : AMBER,
              backgroundColor: "rgba(255,255,255,0.02)",
            }}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 9.5,
                letterSpacing: 1.2,
                textTransform: "uppercase",
                color:
                  offer === "accepted"
                    ? SAGE
                    : offer === "declined" || offer === "withdrawn"
                      ? WS
                      : AMBER,
              }}
            >
              {OFFER_STATUS_LABEL[offer]}
            </Text>
          </View>
        ) : null}
        <View style={{ flex: 1 }} />
        {!offer ? (
          <Pressable
            onPress={onInvite}
            style={[
              {
                paddingHorizontal: 14,
                paddingVertical: 8,
                borderRadius: 3,
                backgroundColor: AMBER,
              } as ViewStyle,
              Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
            ]}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 10.5,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: FOREST,
              }}
            >
              Invite to interview →
            </Text>
          </Pressable>
        ) : offer === "extended" || offer === "viewed" ? (
          <Pressable
            onPress={() => {
              if (busy) return;
              if (withdrawArmed) {
                setWithdrawArmed(false);
                void withdraw();
              } else {
                setWithdrawArmed(true);
                setTimeout(() => setWithdrawArmed(false), 4000);
              }
            }}
            style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 10.5,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: withdrawArmed ? RED : WS,
              }}
            >
              {withdrawArmed ? "Click again to withdraw" : "Withdraw offer"}
            </Text>
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}

function InviteModal({
  target,
  competition,
  onClose,
  onSent,
}: {
  target: PipelineRow;
  competition: { id: string; name: string; company_id: string; company_name: string | null };
  onClose: () => void;
  onSent: () => void;
}) {
  const firstName = (target.student_name ?? "").trim().split(/\s+/)[0] || "there";
  const companyName = competition.company_name ?? "us";
  const defaultMessage =
    `Hi ${firstName},\n\n` +
    `We were impressed with your performance in ${competition.name} ` +
    `and would like to invite you to interview for the ${target.position_title} role at ${companyName}.\n\n` +
    `[Add anything specific you'd like to say to this candidate.]`;

  const [message, setMessage] = useState(defaultMessage);
  const [proposedTimes, setProposedTimes] = useState("");
  const [locationOrLink, setLocationOrLink] = useState("");
  const [deadlineDays, setDeadlineDays] = useState("7");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function send() {
    setErr(null);
    if (!message.trim()) {
      setErr("Add a message before sending.");
      return;
    }
    setSubmitting(true);
    const days = Math.max(1, Math.min(60, Number(deadlineDays) || 7));
    const deadlineAt = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
    const { error } = await extendInterviewOffer({
      positionId: target.position_id,
      studentId: target.student_id,
      competitionId: competition.id,
      companyId: competition.company_id,
      message: message.trim(),
      proposedTimes: proposedTimes.trim() || null,
      locationOrLink: locationOrLink.trim() || null,
      deadlineAt,
    });
    setSubmitting(false);
    if (error) {
      setErr(error.message);
      return;
    }
    onSent();
  }

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onClose}>
      <View
        style={{
          flex: 1,
          backgroundColor: "rgba(0,0,0,0.65)",
          justifyContent: "center",
          paddingHorizontal: 16,
        }}
      >
        <View
          style={{
            maxWidth: 600,
            width: "100%",
            maxHeight: "92%",
            alignSelf: "center",
            backgroundColor: FOREST_MID,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: BORDER,
          }}
        >
          <ScrollView contentContainerStyle={{ padding: 22 }}>
            <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 22, color: LINEN }}>
              Invite {target.student_name ?? "candidate"} to interview
            </Text>
            <Text style={{ marginTop: 4, fontFamily: FONTS.body, fontSize: 12.5, color: WM }}>
              for {target.position_title} at {companyName}
            </Text>

            {err ? (
              <View
                style={{
                  marginTop: 12,
                  paddingHorizontal: 12,
                  paddingVertical: 10,
                  borderRadius: 3,
                  backgroundColor: "rgba(230,154,138,0.08)",
                  borderWidth: 1,
                  borderColor: "rgba(230,154,138,0.4)",
                }}
              >
                <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: RED }}>{err}</Text>
              </View>
            ) : null}

            <FieldLabel required>Message</FieldLabel>
            <DarkInput value={message} onChange={setMessage} multiline placeholder="" />

            <FieldLabel>Proposed times (optional)</FieldLabel>
            <DarkInput
              value={proposedTimes}
              onChange={setProposedTimes}
              placeholder="Mon Jun 1 2pm ET · Tue Jun 2 11am ET · Thu Jun 4 4pm ET"
            />

            <FieldLabel>Location or link (optional)</FieldLabel>
            <DarkInput
              value={locationOrLink}
              onChange={setLocationOrLink}
              placeholder="Zoom (link will be sent on confirmation) · Coravu HQ, Gainesville FL"
            />

            <FieldLabel>Respond-by deadline</FieldLabel>
            <View style={{ marginTop: 6, flexDirection: "row", alignItems: "center", gap: 8 }}>
              <TextInput
                value={deadlineDays}
                onChangeText={setDeadlineDays}
                keyboardType="number-pad"
                style={
                  {
                    width: 64,
                    backgroundColor: FOREST_DEEP,
                    borderWidth: 1,
                    borderColor: BORDER,
                    borderRadius: 3,
                    paddingHorizontal: 10,
                    paddingVertical: 8,
                    color: W,
                    fontFamily: FONTS.displayMedium,
                    fontSize: 18,
                    textAlign: "center",
                    ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as object) : {}),
                  } as unknown as TextStyle
                }
              />
              <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 13, color: WS }}>days from now</Text>
            </View>

            <View style={{ marginTop: 22, flexDirection: "row", justifyContent: "flex-end", gap: 14 }}>
              <Pressable
                onPress={onClose}
                style={[
                  { paddingHorizontal: 14, paddingVertical: 10 },
                  Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                ]}
              >
                <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: WM }}>Cancel</Text>
              </Pressable>
              <Pressable
                onPress={() => void send()}
                disabled={submitting}
                style={[
                  {
                    paddingHorizontal: 18,
                    paddingVertical: 10,
                    borderRadius: 3,
                    backgroundColor: AMBER,
                    opacity: submitting ? 0.5 : 1,
                  } as ViewStyle,
                  Platform.OS === "web" && !submitting ? ({ cursor: "pointer" } as const) : null,
                ]}
              >
                {submitting ? (
                  <ActivityIndicator size="small" color={FOREST} />
                ) : (
                  <Text
                    style={{
                      fontFamily: FONTS.bodyBold,
                      fontSize: 11,
                      letterSpacing: 1.4,
                      textTransform: "uppercase",
                      color: FOREST,
                    }}
                  >
                    Send interview offer →
                  </Text>
                )}
              </Pressable>
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

function FilterPill({ label, active, onPress }: { label: string; active: boolean; onPress: () => void }) {
  return (
    <Pressable
      onPress={onPress}
      style={[
        {
          paddingHorizontal: 12,
          paddingVertical: 7,
          borderRadius: 999,
          borderWidth: 1,
          borderColor: active ? AMBER : BORDER,
          backgroundColor: active ? "rgba(181,135,58,0.12)" : "transparent",
        } as ViewStyle,
        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
      ]}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyMedium,
          fontSize: 11,
          color: active ? AMBER : WS,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function FilterChip({
  label,
  active,
  tone,
  onPress,
}: {
  label: string;
  active: boolean;
  tone?: "sage" | "red";
  onPress: () => void;
}) {
  const accent = tone === "sage" ? SAGE : tone === "red" ? RED : AMBER;
  return (
    <Pressable
      onPress={onPress}
      style={[
        {
          paddingHorizontal: 10,
          paddingVertical: 5,
          borderRadius: 3,
          borderWidth: 1,
          borderColor: active ? accent : BORDER,
          backgroundColor: active ? "rgba(255,255,255,0.04)" : "transparent",
        } as ViewStyle,
        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
      ]}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9.5,
          letterSpacing: 1.2,
          textTransform: "uppercase",
          color: active ? accent : WS,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function FieldLabel({ children, required }: { children: string; required?: boolean }) {
  return (
    <View style={{ marginTop: 14, flexDirection: "row", alignItems: "center", gap: 4 }}>
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9.5,
          letterSpacing: 1.6,
          textTransform: "uppercase",
          color: WM,
        }}
      >
        {children}
      </Text>
      {required ? <Text style={{ color: RED, fontSize: 10 }}>*</Text> : null}
    </View>
  );
}

function DarkInput({
  value,
  onChange,
  placeholder,
  multiline,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
}) {
  return (
    <TextInput
      value={value}
      onChangeText={onChange}
      placeholder={placeholder}
      placeholderTextColor={WL}
      multiline={multiline}
      style={
        {
          marginTop: 6,
          minHeight: multiline ? 140 : undefined,
          backgroundColor: FOREST_DEEP,
          borderWidth: 1,
          borderColor: BORDER,
          borderRadius: 4,
          paddingHorizontal: 12,
          paddingVertical: 10,
          color: W,
          fontFamily: FONTS.body,
          fontSize: 13,
          lineHeight: 20,
          textAlignVertical: multiline ? "top" : undefined,
          ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as object) : {}),
        } as unknown as TextStyle
      }
    />
  );
}
